<template>
    <div class="w-full" :class="{
        'mt-6': !adminOverride
    }">
        <div class="lg:flex justify-between items-center" v-if="!adminOverride">
            <p class="font-hero text-24">{{ title }}</p>
            <button class="mt-2 text-15 link-text keep" type="button" @click="examples = !examples">
                {{ examples ? 'Close' : 'See' }} {{ $help.pluralize('example', examplaesCount) }}
            </button>
        </div>

        <!-- :class='{
                "grid-cols-1": minBoxes == 1,
                "grid-cols-1 lg:grid-cols-3": minBoxes != 1,
            }' -->

        <div
            class="grid gap-3 grid-cols-1"
            :class="{
                'lg:grid-cols-3': cols == 3 || (examples && examplaesCount > 2),
                'lg:grid-cols-2': examples && examplaesCount == 2,
                'mt-6': !adminOverride
            }"
        >
            <div
                class="w-full min-h-[178px] border-grey-500 flex items-center justify-center cursor-pointer relative aspect-square"
                v-for="img in imagesToShow"
                :class="{
                    'h-full border-none': examples,
                    'h-178 border': !examples && folderId != 101,
                    'h-full border': !examples && folderId == 101,
                }"
            >
                <FilePreview :img="img" :example="examples ? img : false" :type="title" />

                <div
                    class="absolute p-2 inset-0 bg-black/50 flex justify-end opacity-0 hover:opacity-100 transition-opacity duration-300"
                    v-if="!examples" @click='() => $emit("selectedImage", img)'
                >
                    <button @click.stop="destroy(img)" v-if="!examples" class="w-4.5 h-4.5 z-10" type="button">
                        <IconClose class="stroke-white" />
                    </button>
                </div>
            </div>

            <template v-for="img in uploadingFiles.filter((f) => f.error == null && f.uploading)">
                <div
                    class="w-full min-h-[178px] border border-grey-500 flex items-center justify-center cursor-pointer relative aspect-square"
                    :class="{
                        'h-full border-none': examples,
                        'h-178 border': !examples && folderId != 101,
                        'h-full border': !examples && folderId == 101,
                    }"
                >
                    <Vue3Lottie v-if="img.uploading" :animationData="ZapoLogoJson" :height="40" :width="40" :noMargin="true" />
                    
                    <template v-else>
                        <FilePreview :img="img.upload" :example="examples ? img : false" />

                        <div
                            class="absolute p-2 inset-0 bg-black/50 flex justify-end opacity-0 hover:opacity-100 transition-opacity duration-300"
                        >
                            <button @click.stop="destroyUpload(img)" v-if="!examples" class="w-4.5 h-4.5 z-10" type="button">
                                <IconClose class="stroke-white" />
                            </button>
                        </div>
                    </template>
                </div>
            </template>

            <div
                class="w-full h-178 border border-grey-500 flex items-center justify-center cursor-pointer relative aspect-square"
                v-if="!examples"
                v-for="sub in showUploadBoxes"
            >
                <input
                    type="file"
                    accept="image/*,.ai,.pdf"
                    @change="upload"
                    :multiple="minBoxes != 1"
                    class="opacity-0 absolute inset-0 w-full cursor-pointer"
                />

                <div>
                    <div class="stroke-green-500 w-8 h-8 rounded-full bg-green-500 bg-opacity-10 flex items-center justify-center m-auto">
                        <IconPlus class="w-4.5 h-4.5" />
                    </div>

                    <p class="text-14 mt-2" v-if="minBoxes == 1">{{ uploadText }}</p>
                </div>
            </div>
        </div>
        
        <template v-for="error in uploadingFiles.filter((f) => f.error != null)">
            <Error
                class="col-span-1 mt-2"
                :error="error.error"
            />
        </template>

        <Error :error="error" v-if="error !== false" />

        <template v-if="!adminOverride">
            <div class="flex justify-between items-center mt-10 cursor-pointer" @click="format = !format">
                <p class="font-sub text-19">{{ formatTitle }}</p>

                <button class="cursor-pointer" type="button">
                    <component :is="format ? 'IconArrowUp' : 'IconArrowDown'" class="stroke-black w-4.5 h-4.5" />
                </button>
            </div>

            <ul class="text-15 text-grey-900 mt-4" v-if="format">
                <li class="flex pb-2" v-for="(h, hi) in help">
                    <IconTrue
                        class="w-5 h-5 rounded-full mt-0.5 stroke-green-600 bg-green-500 bg-opacity-10 shrink-0"
                        v-if="helpNoIndex == false || helpNoIndex.indexOf(hi) == -1"
                    />
                    <IconFalse class="w-5 h-5 rounded-full mt-0.5 stroke-red-700 bg-red-600 bg-opacity-10 shrink-0" v-else />
                    <p class="pl-3" v-html="h" />
                </li>

                <slot name="extraHelp" />
            </ul>

            <div class="border-b border-grey-500 mt-4" />

            <InputCheckbox
                :name="dontName"
                :checked="company[dontName]"
                :value="1"
                class="mt-6"
                v-if="canBeNull == true"
                :hideError="this.totalUploadCount > 0"
                :hideErrorClass="this.totalUploadCount > 0"
                :label="true"
            >
                <p class="text-14 text-grey-900 pl-2">{{ dontText }}</p>
            </InputCheckbox>

            <input type="hidden" :name="type + '_count'" :value="totalUploadCount" />
        </template>

        <template v-if="adminOverride && company[dontName]">
            <div class="flex items-center gap-2 mt-4">
                <IconInfo class="w-4.5 h-4.5 stroke-grey-900" />
                <p class="text-14 text-grey-900">Customer has stated they don't have any images</p>
            </div>
        </template>
    </div>
</template>

<script>
// import * as Vibrant from 'node-vibrant'
import ZapoLogoJson from '~/assets/animation-zapo-logo.json'
import Upload from '~/models/upload.js'

export default {
    props: {
        title: String,
        formatTitle: String,
        help: Array,
        company: Object,
        type: {
            type: String,
            default: 'logos',
        },
        minBoxes: {
            type: Number,
            default: 3,
        },
        images: {
            type: Array,
            default: function () {
                return []
            },
        },
        dontName: String,
        dontText: String,
        uploadText: { type: String, default: 'Click to add your logo' },
        canBeNull: { type: Boolean, default: true },
        folderId: { type: Number, default: null },
        helpNoIndex: { type: Array, default: false },
        industry: { type: Object, default: null },
        cols: { type: Number, default: 3 },
        maxUploads: { type: Number, default: -1 },
        adminOverride: { type: Boolean, default: false },
    },

    created() {
        this.setImages(this.images)

        if (this.industry != null) {
            this.exampleImages.work = [
                '/examples/' + this.industry.name + '/gallery-1.png',
                '/examples/' + this.industry.name + '/gallery-2.png',
                '/examples/' + this.industry.name + '/gallery-3.png',
                '/examples/' + this.industry.name + '/gallery-4.png',
                '/examples/' + this.industry.name + '/gallery-5.png',
                '/examples/' + this.industry.name + '/gallery-6.png',
            ]
        }
    },

    data() {
        return {
            format: false,
            uploadingFiles: [],
            lImages: null,
            view: null,
            examples: false,
            ZapoLogoJson,
            exampleImages: {
                logos: [
                    '/examples/logo1.png',
                    '/examples/logo2.png',
                    '/examples/logo3.png',
                    '/examples/logo4.png',
                    '/examples/logo5.png',
                    '/examples/logo6.png',
                    '/examples/logo7.png',
                    '/examples/logo8.png',
                    '/examples/logo9.png',
                    '/examples/logo10.png',
                    '/examples/logo11.png',
                    '/examples/logo12.png',
                ],
                vans: [
                    '/examples/van1.png',
                    '/examples/van2.png',
                    '/examples/van3.png',
                    '/examples/van4.png',
                    '/examples/van5.png',
                    '/examples/van6.png',
                ],
                work: [],
                team: [
                    '/examples/team1.png',
                    '/examples/team2.png',
                    '/examples/team3.png',
                    '/examples/team4.png',
                    '/examples/team5.png',
                    '/examples/team6.png',
                ],
                certificate_of_incorporation: ['/examples/company-certificate-example.png'],
                identification: ['/examples/driving-license.png', '/examples/passport.png'],
                insurance: ['/examples/simply-business-pli.png', '/examples/hiscox-pli.png'],
                address: ['/examples/utility-bill.png', '/examples/bank-statement.png', '/examples/council-tax.png'],
            },
        }
    },

    methods: {
        setImages(images) {
            // console.log('images', images)

            this.lImages = images
        },

        upload(event) {
            // this.uploadingFiles.forEach(f => {
            //     console.log(f)
            //     // return f.error = null
            // })

            var i = this.uploadingFiles.length
            while (i--) {
                if (this.uploadingFiles[i].error != null) {
                    this.uploadingFiles.splice(i, 1)
                }
            }

            for (let i = 0; i < event.target.files.length; i++) {
                this._uploadFile(event.target.files[i]);
            }
        },

        _uploadFile(file) {
            let uploadFile = {
                index: this.uploadingFiles.length,
                uploading: true,
                error: null
            };

            this.uploadingFiles.push(uploadFile)

            let formData = new FormData()
            formData.append('belongs_type', 2)
            formData.append('belongs_id', this.company.id)
            formData.append('file', file)
            formData.append('folder_id', this.folderId)

            this.$axios
                .post('/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((res) => {
                    this.lImages.push(res)
                    this.uploadingFiles[uploadFile.index].uploading = false
                })
                .catch((error) => {
                    this.uploadingFiles[uploadFile.index].uploading = false
                    this.uploadingFiles[uploadFile.index].error = error.data.message
                    console.log(error.data)
                })
        },

        destroy(img) {
            this.$axios.delete('/upload/' + img.id).then((res) => {
                this.lImages = res.filter((i) => i.folder_id == this.folderId)
            })
        },

        destroyUpload(upload) {
            this.destroy(upload.upload)
            this.uploadingFiles.splice(
                this.uploadingFiles.findIndex((f) => f == upload),
                1,
            )
        },
    },

    computed: {
        imagesToShow() {
            if (this.examples) {
                return this.exampleImages[this.type]
            } else {
                return this.lImages
            }
        },

        examplaesCount() {
            return this.exampleImages[this.type].length
        },

        totalUploadCount() {
            return this.lImages.length + this.uploadingFiles.filter((f) => f.error == null).length
        },

        showUploadBoxes() {
            console.log({
                minBoxes: this.minBoxes,
                lImages: this.lImages.length,
                uploadingFiles: this.uploadingFiles.filter((f) => f.error == null).length,
                maxUploads: this.maxUploads,
            })

            if (this.adminOverride) {
                return 1
            }

            let totalUploads = this.lImages.length + this.uploadingFiles.filter((f) => f.error == null).length

            if (this.maxUploads != -1 && totalUploads >= this.maxUploads) {
                return 0
            } else if (this.maxUploads != -1) {
                return this.maxUploads
            } else {
                return totalUploads % 3 == 0 ? 3 : 3 - (totalUploads % 3)
            }
        },

        error() {
            if (this.maxUploads == 1 && this.totalUploadCount > 0) {
                return false
            }

            let inputForm = this.$help.getComponent(this, 'InputForm')

            return inputForm != null ? inputForm.$data.errors.has(this.type + '_count') : false
        },
    },

    watch: {
        images() {
            this.setImages(this.images)
        },
    },
}
</script>
